import { IGameAttendances, defaultPointsBreakDown, pointsTypeMapper } from "@grassrootly/api";
import { Div, Label, Icon, Touchable } from "@grassrootly/rn-core";
import React from "react";
import { Image, LayoutAnimation, View } from "react-native";
import { GamePlayerProps } from "./GameAttendees.types";
import { images } from "../../../layout/assets";
import { PlayerRating } from "../../shared";

export default function Player({ attendee, showTeam = false, showPoints = false, hostTeamName, hostTeamLogo, visitorTeamName, visitorTeamLogo, onPlayerPress }: GamePlayerProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const onExpandPress = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded(!isExpanded);
  };
  
  const handlePlayerPress = () => {
    if (onPlayerPress && attendee.player_id) {
      onPlayerPress(attendee.player_id);
    }
  };
  
  // Determine which team this player belongs to
  const playerTeamName = attendee.team === 'HOST' ? hostTeamName : attendee.team === 'VISITOR' ? visitorTeamName : null;
  const playerTeamLogo = attendee.team === 'HOST' ? hostTeamLogo : attendee.team === 'VISITOR' ? visitorTeamLogo : null;
  
  return (
    <Div classNames={["flex1"]}>
      <Div classNames={["flexRow", "alignItemsCenter"]}>
        {/* Player avatar and info - clickable for profile navigation */}
        <Touchable
          classNames={["flexRow", "alignItemsCenter", "flex1"]}
          onPress={handlePlayerPress}
          touchedOpacity={0.9}
          disabled={!onPlayerPress}
        >
          <Image
            style={{ height: 40, width: 40, borderRadius: 40 }}
            resizeMode="cover"
            source={attendee.player?.profile_picture ? { uri: attendee.player?.profile_picture } : images.playerAvatar}
          />
          <Div classNames={["ml-{15}", "flex1"]}>
          <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
            <Label numberOfLines={1} ellipsizeMode="tail">
              {attendee.player?.name}
              {attendee.team && showTeam ? ` (${attendee.team == "HOST" ? "Beli" : "Markeri"})` : ""}
            </Label>
            {attendee.position === 'GK' ? (
              <Div classNames={["bgPrimaryMain", "rounded", "ph8", "pv2"]}>
                <Label type="l3Bold" classNames={["colorNeutralLighterLight"]}>GK</Label>
              </Div>
            ) : null}
          </Div>
          {playerTeamName ? (
            <Div classNames={["flexRow", "alignItemsCenter", "gap8", "mt-{4}"]}>
              {playerTeamLogo ? (
                <Image
                  style={{ height: 16, width: 16, borderRadius: 4 }}
                  resizeMode="cover"
                  source={{ uri: playerTeamLogo }}
                />
              ) : null}
              <Label type="l4Regular" classNames={["colorNeutralLighterDark"]}>
                {playerTeamName}
              </Label>
            </Div>
          ) : null}
          <Div classNames={["flexRow", "mt-{8}", "gap8", "alignItemsCenter"]}>
            {attendee.goals_scored ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <Icon name="sports_soccer" classNames={["mr-{2}"]} size={18} />
                <Label type="l3Regular">{attendee.goals_scored > 1 ? `x${attendee.goals_scored}` : ""}</Label>
              </Div>
            ) : null}
            {attendee.assists_made ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <Image source={images.assist} style={{ marginRight: 3, height: 14, width: 24 }} />
                <Label type="l3Regular">{attendee.assists_made > 1 ? `x${attendee.assists_made}` : ""}</Label>
              </Div>
            ) : null}
            {attendee.own_goals ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <Icon name="sports_soccer" color="#DC2626" classNames={["mr-{2}"]} size={18} />
                <Label type="l3Regular">{attendee.own_goals > 1 ? `x${attendee.own_goals}` : ""}</Label>
              </Div>
            ) : null}
            {attendee.yellow_cards ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <View style={{ width: 10, height: 14, backgroundColor: '#FFC107', borderRadius: 2, marginRight: 4 }} />
                <Label type="l3Regular">{attendee.yellow_cards > 1 ? `x${attendee.yellow_cards}` : ""}</Label>
              </Div>
            ) : null}
            {attendee.red_cards ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <View style={{ width: 10, height: 14, backgroundColor: '#DC2626', borderRadius: 2, marginRight: 4 }} />
                <Label type="l3Regular">{attendee.red_cards > 1 ? `x${attendee.red_cards}` : ""}</Label>
              </Div>
            ) : null}
            {attendee.errors_leading_to_a_goal ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <Icon name="fmd_bad" color="#DC2626" classNames={["mr-{2}"]} size={18} />
                <Label type="l3Regular">{attendee.errors_leading_to_a_goal > 1 ? `x${attendee.errors_leading_to_a_goal}` : ""}</Label>
              </Div>
            ) : null}
            {attendee.saves ? (
              <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
                <Icon name="front_hand" classNames={["mr-{2}"]} size={18} />
                <Label type="l3Regular">{attendee.saves > 1 ? `x${attendee.saves}` : ""}</Label>
              </Div>
            ) : null}
          </Div>
          </Div>
        </Touchable>
        
        {/* Points and expand arrow - clickable for XP details */}
        {showPoints ? (
          <Touchable
            classNames={["ml-{auto}", "flexRow", "alignContentCenter", "justifyContentCenter"]}
            onPress={onExpandPress}
            touchedOpacity={0.9}
          >
            <Div>
              {typeof attendee.rating == "number" ? (
                <PlayerRating rating={attendee.rating} classNames={["mb-{3}", "ml-{auto}"]} />
              ) : null}
              <Label type="l3Regular">Exp +{attendee.points_earned}</Label>
            </Div>
            <Icon
              name={isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
              classNames={["ml-{8}", "mv-{auto}"]}
            />
          </Touchable>
        ) : null}
      </Div>
      {isExpanded ? (
        <Div classNames={["pt-{12}", "ml-{55}", "flexRow", "alignContentCenter"]}>
          <Div classNames={["mr-{24}"]}>
            {(attendee.points_earned_breakdown || defaultPointsBreakDown)
              .filter((category) => category.total > 0)
              .map((category, i) => {
                return (
                  <Label key={i} type="l2Regular" classNames={["mb-{4}"]}>
                    {pointsTypeMapper[category.type]}
                  </Label>
                );
              })}
          </Div>
          <Div classNames={["ml-{auto}", "mr-{24}"]}>
            {(attendee.points_earned_breakdown || defaultPointsBreakDown)
              .filter((category) => category.total > 0)
              .map((category, i) => (
                <Label key={i} type="l2Regular" classNames={["mb-{4}", "textLeft"]}>
                  {category.total} pts
                </Label>
              ))}
          </Div>
        </Div>
      ) : null}
    </Div>
  );
}
